import React, { useState, useEffect, useMemo } from "react";
import { MdSearchOff, MdFilterList, MdDelete, MdEdit } from "react-icons/md";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";
import Pagination from "../Pagination/Pagination";
import OptionsMenu from "../OptionsMenu/OptionsMenu";
import Badge from "../Badge/Badge";
import "./Table.scss";
import InputSearch from "../Inputs/InputSearch/InputSearch";
import {
    BadgeProps,
    FilterField,
    FormGroup,
    NotificationHandle,
} from "../../../types/types";
import type { ReactNode } from "react";
import { useTableCRUD } from "../../../utils/useTableCRUD";
import Button from "../Button/Button";
import Modal from "../Modal/Modal";
import FormModal from "../FormModal/FormModal";
import { IoMdAdd } from "react-icons/io";
import { Link, useLocation } from "react-router-dom";
import { hasFileInData, mappedDataToFormData } from "../../../utils/utils";
import FiltersDrawer from "../FiltersDrawer/FiltersDrawer";
import Notification from "../notification/notification";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import InputDropdown_New from "../../inputs/InputDropdown/InputDropdown_New";
import InputTextarea from "../Inputs/InputTextarea/InputTextarea";
import { useZone } from "../../../context/ZoneContext";
import { getApiInstance } from "../../../utils/api";
import { TiDeleteOutline } from "react-icons/ti";
import { MdOutlineEdit } from "react-icons/md";
import { MdOutlineFileDownload } from "react-icons/md";
import { applyTableSearch } from "../../../utils/TableUtils/tableSearch";

type CustomAction<T> = {
    label: string;
    icon?: ReactNode;
    action?: (row: T) => void;
    render?: (row: T) => ReactNode;
};

type ActionItem<T> = "Eliminar" | "Editar" | "Exportar" | CustomAction<T>;

type Column<T> = {
    key: keyof T | string[];
    label: string;
    visible: boolean;
    isLink?: boolean;
    sortable?: boolean;
    documentProp?: string;
    align?: "left" | "center" | "right";
    width?: string;
    type?: "text" | "number" | "date" | "status" | "badge" | "document";
    linkPath?: (row: T) => string;
    render?: (row: T) => ReactNode;
    badgeConfig?: {
        [key: string]: { variant: string; icon?: ReactNode; label?: string };
    } & { default?: { variant: string; icon?: ReactNode; label?: string } };
    badgeProps?: (row: T) => { type: string; label: string; Icon?: ReactNode };
    bold?: boolean;
    secondary?: keyof T | string[];
    visibleOnExpand?: boolean;
};

type TableProps<T> = {
    columns: Column<T>[];
    dataTable: T[];
    noResultsMessage?: string;
    rowsPerPage?: number;
    topContent?: ReactNode;
    showCheckboxes?: boolean;
    searchTerms?: string[];
    deleteApiUrl?: string;
    addApiUrl?: string;
    editApiUrl?: string;
    formElements?: FormGroup[];
    actions?: ActionItem<T>[];
    elementName?: string;
    keyProp?: string;
    sortProp?: string;
    states?: BadgeProps[];
    stateProp: string;
    filterFields?: FilterField<T>[];
    loading: boolean;
    selectedRows?: Set<React.Key>;
    onSelectionChange?: (newSelection: Set<React.Key>) => void;
    stateChangeUrl?: string;
    stateChangeOptions?: { value: any; label: string }[];
    expandable?: boolean;
    expandableBreakpoint?: number;
    renderExpandedContent?: (row: T) => ReactNode;
    userCodAcceso?: number;
    userCodPerfil?: number;
    exportApiUrl?: string;
    extraPropExportKey?: string;
    extraPropExportText?: string;
    onDataUpdate?: (newData: T[]) => void;
    extraTopButtons?: ReactNode;
};

function Table<T extends { id: number }>({
    columns,
    dataTable = [],
    onDataUpdate = () => {},
    noResultsMessage = "No hay resultados.",
    rowsPerPage = 10,
    topContent,
    showCheckboxes = false,
    searchTerms = [],
    formElements,
    actions,
    elementName,
    keyProp = "",
    states,
    stateProp,
    addApiUrl,
    editApiUrl,
    deleteApiUrl,
    filterFields = [],
    loading = false,
    sortProp = keyProp,
    selectedRows,
    onSelectionChange,
    stateChangeUrl,
    stateChangeOptions,
    expandable = false,
    expandableBreakpoint = 1024,
    renderExpandedContent,
    userCodAcceso,
    userCodPerfil,
    exportApiUrl,
    extraPropExportKey = "",
    extraPropExportText = "",
    extraTopButtons,
}: TableProps<T>) {
    const {
        data,
        createItem,
        updateItem,
        deleteItem,
        isLoading,
        error,
        updateLocalData,
    } = useTableCRUD<T>(dataTable);

    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = useState(1);
    const [selectAll, setSelectAll] = useState(false);
    const [filteredData, setFilteredData] = useState<T[]>(dataTable);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [filterValues, setFilterValues] = useState<Record<string, any>>({});
    const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
    const [rowToDelete, setRowToDelete] = useState<T | null>(null);
    const [isStateModalOpen, setIsStateModalOpen] = useState(false);
    const [stateModalRow, setStateModalRow] = useState<T | null>(null);
    const [newStateValue, setNewStateValue] = useState<any>(null);
    const [observations, setObservations] = useState<string>("");
    const [isChangingState, setIsChangingState] = useState(false);
    const [missingNames, setMissingNames] = useState<string[]>([]);
    const [expandedRows, setExpandedRows] = useState<Set<React.Key>>(new Set());
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
    const [isSmallMobile, setIsSmallMobile] = useState(
        window.innerWidth <= 480
    );
    const { selectedZone } = useZone();
    const [searchTerm, setSearchTerm] = useState("");

    const [isBulkStateChange, setIsBulkStateChange] = useState(false);

    const canChangeState =
        !!stateChangeUrl &&
        !!stateChangeOptions &&
        userCodAcceso === 1 &&
        userCodPerfil !== 4;

    const enableRowSelection = showCheckboxes || canChangeState;

    const [internalSelectedRows, setInternalSelectedRows] = useState<
        Set<React.Key>
    >(new Set());

    const isSelectionControlled =
        selectedRows !== undefined && onSelectionChange !== undefined;

    const effectiveSelectedRows = isSelectionControlled
        ? selectedRows
        : internalSelectedRows;

    const setSelection = (newSelection: Set<React.Key>) => {
        if (isSelectionControlled) {
            onSelectionChange?.(newSelection);
        } else {
            setInternalSelectedRows(newSelection);
        }
    };

    const notificationRef = useRef<NotificationHandle | null>(null);

    const loadingData = loading;

    const location = useLocation();
    const base = location.pathname;

    useEffect(() => {
        setDynamicFormElements(formElements || []);
    }, [formElements]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filteredData]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= expandableBreakpoint);
            setIsSmallMobile(window.innerWidth <= 480);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [expandableBreakpoint]);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalData, setModalData] = useState<{ [key: string]: any }>({});
    const [modalType, setModalType] = useState("add");
    const [dynamicFormElements, setDynamicFormElements] = useState<FormGroup[]>(
        formElements || []
    );
    const [associatedOptions, setAssociatedOptions] = useState<{
        [key: string]: any[];
    }>({});

    const hasFilters = (filterFields?.length ?? 0) > 0;

    useEffect(() => {
        setCurrentPage(1);
    }, [dataTable]);

    const [sortConfig, setSortConfig] = useState<{
        key: keyof T | null;
        direction: "ascending" | "descending" | null;
    }>({
        key: null,
        direction: null,
    });

    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;

    const sortedData = useMemo(() => {
        if (
            !filteredData ||
            filteredData == undefined ||
            filteredData.length == 0
        ) {
            return [];
        }

        let sortableData = [...filteredData];

        if (sortConfig.key && sortConfig.direction) {
            sortableData.sort((a, b) => {
                const key = sortConfig.key as keyof T;
                if (a[key] < b[key]) {
                    return sortConfig.direction === "ascending" ? -1 : 1;
                }
                if (a[key] > b[key]) {
                    return sortConfig.direction === "ascending" ? 1 : -1;
                }
                return 0;
            });
        }

        return sortableData;
    }, [filteredData, sortConfig]);

    const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);

    const requestSort = (key: keyof T) => {
        let direction: "ascending" | "descending" | null = "ascending";
        if (sortConfig.key === key) {
            direction =
                sortConfig.direction === "ascending" ? "descending" : null;
        }
        setSortConfig({ key: direction ? key : null, direction });
    };

    const toggleRowExpansion = (rowId: React.Key) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(rowId)) {
            newExpanded.delete(rowId);
        } else {
            newExpanded.add(rowId);
        }
        setExpandedRows(newExpanded);
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelection(new Set());
        } else {
            const allIds = new Set(
                filteredData.map((row) => row[keyProp as keyof T] as React.Key)
            );
            setSelection(allIds);
        }
    };

    const handleSelectRow = (rowId: React.Key) => {
        const newSelected = new Set(effectiveSelectedRows);

        if (newSelected.has(rowId)) {
            newSelected.delete(rowId);
        } else {
            newSelected.add(rowId);
        }

        setSelection(newSelected);
    };

    const renderStatusBadge = (status: string) => {
        const statusMap: { [key: string]: string } = {
            approved: "approved",
            "in review": "in-review",
            draft: "draft",
            rejected: "rejected",
        };

        const badgeClass = statusMap[status.toLowerCase()] || "draft";

        return <span className={`status-badge ${badgeClass}`}>{status}</span>;
    };

    const getNestedValue = (obj: any, path: keyof any | string[]): any => {
        if (Array.isArray(path)) {
            return path.reduce((acc, part) => acc && acc[part], obj);
        }
        return obj[path];
    };

    const getCodTipoValidacion = () => {
        const elementIdentificator = addApiUrl?.split("/")[2] || "";

        switch (elementIdentificator) {
            case "ObraDocumento":
                return 1;
            case "ResiduosObra":
                return 2;
            case "GestorDocumentos":
                return 7;
            default:
                return 0;
        }
    };

    const buildStateDto = (row: T) => {
        const rowAny = row as any;
        const id = rowAny[keyProp];

        return {
            CodTipoValidacion: getCodTipoValidacion(),
            CodClave: Number(rowAny.CodClave ?? id) || 0,
            Observaciones: String(observations ?? ""),
            CodEstado: newStateValue?.value,
            CodZona: selectedZone?.value ?? 0,
            CodZonaLegal: selectedZone?.value ?? 0,
        };
    };

    const openBulkStateModal = () => {
        if (!canChangeState || effectiveSelectedRows.size === 0) return;

        setIsBulkStateChange(true);
        setStateModalRow(null);
        setNewStateValue(null);
        setObservations("");
        setIsStateModalOpen(true);
    };

    const openStateModal = (row: T) => {
        if (canChangeState) {
            setIsBulkStateChange(false);
            setStateModalRow(row);
            const current = (row as any)[stateProp];
            setNewStateValue(
                stateChangeOptions?.find((x) => x.value == current) ?? null
            );
            setObservations("");
            setIsStateModalOpen(true);
        }
    };

    const closeStateModal = () => {
        setIsStateModalOpen(false);
        setStateModalRow(null);
        setNewStateValue(null);
        setObservations("");
        setIsBulkStateChange(false);
    };

    useEffect(() => {
        if (!enableRowSelection) {
            setSelectAll(false);
            return;
        }

        const allFilteredIds = filteredData.map(
            (row) => row[keyProp as keyof T] as React.Key
        );

        setSelectAll(
            allFilteredIds.length > 0 &&
                allFilteredIds.every((id) => effectiveSelectedRows.has(id))
        );
    }, [filteredData, effectiveSelectedRows, keyProp, enableRowSelection]);

    const confirmStateChange = async () => {
        if (
            !stateChangeUrl ||
            newStateValue === null ||
            newStateValue === undefined
        ) {
            return;
        }

        try {
            setIsChangingState(true);

            const sourceData = (data ?? []) as T[];

            if (isBulkStateChange) {
                const rowsToUpdate = sourceData.filter((row: any) =>
                    effectiveSelectedRows.has(row[keyProp] as React.Key)
                );

                if (rowsToUpdate.length === 0) return;

                const dtos = rowsToUpdate.map(buildStateDto);

                stateChangeUrl = "/api/Validaciones/SetValidaciones";

                await createItem(stateChangeUrl, dtos, keyProp, sortProp, {
                    mutate: false,
                });

                const selectedIds = new Set(
                    rowsToUpdate.map((r: any) => String(r[keyProp]))
                );

                setFilteredData((prev) =>
                    prev.map((r: any) =>
                        selectedIds.has(String(r[keyProp]))
                            ? { ...r, [stateProp]: Number(newStateValue.value) }
                            : r
                    )
                );

                if (Array.isArray(data)) {
                    const updated = data.map((r: any) =>
                        selectedIds.has(String(r[keyProp]))
                            ? { ...r, [stateProp]: Number(newStateValue.value) }
                            : r
                    );
                    updateLocalData(updated);
                    onDataUpdate?.(updated);
                }

                setSelection(new Set());
                notificationRef.current?.addNotification(
                    "Estados actualizados correctamente",
                    "success"
                );
            } else {
                if (!stateModalRow) return;

                const rowAny = stateModalRow as any;
                const id = rowAny[keyProp];
                const dto = buildStateDto(stateModalRow);

                await createItem(stateChangeUrl, dto, keyProp, sortProp, {
                    mutate: false,
                });

                setFilteredData((prev) =>
                    prev.map((r: any) =>
                        String(r?.[keyProp]) === String(id)
                            ? { ...r, [stateProp]: Number(newStateValue.value) }
                            : r
                    )
                );

                if (Array.isArray(data)) {
                    const updated = data.map((r: any) =>
                        String(r?.[keyProp]) === String(id)
                            ? { ...r, [stateProp]: Number(newStateValue.value) }
                            : r
                    );
                    updateLocalData(updated);
                    onDataUpdate?.(updated);
                }

                notificationRef.current?.addNotification(
                    "Estado actualizado correctamente",
                    "success"
                );
            }

            closeStateModal();
        } catch (e) {
            console.error("Error al cambiar estado:", e);
            notificationRef.current?.addNotification(
                "No se pudo actualizar el estado",
                "error"
            );
        } finally {
            setIsChangingState(false);
        }
    };

    const openModal = () => {
        setIsModalOpen(true);
        setModalType("add");
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setMissingNames([]);
        setModalData({});
    };

    const renderCellValue = (row: T, column: Column<T>): ReactNode => {
        var value = getNestedValue(row, column.key);

        if (column.render) {
            return column.render(row);
        }

        if (column.isLink && value) {
            const href = `${base}/${(row as Record<string, any>)[keyProp]}`;
            return (
                <Link className="link-style" to={href}>
                    {String(value)}
                </Link>
            );
        }

        if (column.type === "date" && value) {
            return new Date(String(value)).toLocaleDateString();
        }

        if (column.type === "status" && value) {
            return renderStatusBadge(String(value));
        }

        if (
            column.type === "document" &&
            value &&
            column.documentProp &&
            row[column.documentProp as keyof T]
        ) {
            const base64 = row[column.documentProp as keyof T] as string;
            const mime = "application/pdf";
            const href = `data:${mime};base64,${base64}`;
            const fileName = String(value);
            return (
                <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={fileName}
                >
                    {fileName || "Ver documento"}
                </a>
            );
        }

        if (stateProp == column.key && states) {
            if (states && states[(row as Record<string, any>)[stateProp]]) {
                const stateObj =
                    states[(row as Record<string, any>)[stateProp]];

                if (
                    stateChangeOptions === undefined ||
                    stateChangeUrl === undefined
                ) {
                    return (
                        <Badge variant={stateObj.type} Icon={stateObj.Icon}>
                            {stateObj.label}
                        </Badge>
                    );
                }
                return (
                    <span
                        onClick={(e) => {
                            e.stopPropagation();
                            openStateModal(row);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                openStateModal(row);
                            }
                        }}
                        role="button"
                        tabIndex={0}
                        style={{
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                        }}
                        aria-label="Cambiar estado"
                        title={
                            isBulkStateChange
                                ? `Cambiar estado (${effectiveSelectedRows.size} seleccionados)`
                                : "Cambiar estado"
                        }
                    >
                        <Badge variant={stateObj.type} Icon={stateObj.Icon}>
                            {stateObj.label}
                        </Badge>
                    </span>
                );
            }
        }

        if (value === undefined && column.key.toString().includes("/")) {
            const keys = column.key.toString().split("/");
            const key1 = keys[0];
            const key2 = keys[1];

            const nestedObject = row[key1 as keyof T] as Record<string, any>;

            if (nestedObject != undefined) {
                value = nestedObject[key2];
            }
        }

        if (
            Array.isArray(column.key) &&
            column.key.every((k) => typeof k === "string")
        ) {
            const keys = column.key as string[];

            const mainKeys = keys.slice(0, 2); // 0 y 1
            const secondaryKey = keys[2]; // 2 (si existe)

            const mainText = mainKeys
                .map((k) => {
                    const v = resolveValueFromKey(row, k);
                    return v !== null && v !== undefined ? String(v) : "";
                })
                .filter(Boolean)
                .join(" - ");

            const secondaryText =
                secondaryKey !== undefined
                    ? (() => {
                          const v = resolveValueFromKey(row, secondaryKey);
                          return v !== null && v !== undefined ? String(v) : "";
                      })()
                    : "";

            // Si no hay nada, devuelve vacío
            if (!mainText && !secondaryText) return "";

            return (
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        lineHeight: 1.2,
                    }}
                >
                    <div
                        style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {mainText}
                    </div>

                    {secondaryText ? (
                        <div
                            style={{
                                marginTop: 2,
                                color: "rgba(0,0,0,0.55)", // sombreado/gris
                                fontSize: 12,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                            title={secondaryText} // tooltip al pasar
                        >
                            {secondaryText}
                        </div>
                    ) : null}
                </div>
            );
        }

        return value !== undefined &&
            value !== null &&
            (typeof value === "string" ||
                typeof value === "number" ||
                typeof value === "boolean")
            ? String(value)
            : value !== undefined && value !== null
              ? String(value)
              : "";
    };

    const resolveValueFromKey = (row: any, key: string) => {
        if (key.includes("/")) {
            return getNestedValue(row, key.split("/"));
        }
        return row?.[key];
    };

    const getTableClasses = () => {
        const classes = ["custom-table"];

        classes.push(`columns-${columns.length}`);

        if (enableRowSelection) {
            classes.push("has-checkboxes");
        }

        return classes.join(" ");
    };

    const getColumnClass = (isCheckbox = false, isActions = false) => {
        if (isCheckbox) return "checkbox-column";
        if (isActions) return "actions-column";
        return "content-column";
    };

    const handleSearch = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const mappedData = () => {
        const defaults: { [key: string]: any } =
            formElements === undefined
                ? {}
                : formElements
                      .flatMap((group) => group.elements)
                      .filter(
                          (el) =>
                              el.defaultValue !== undefined &&
                              el.visible === false
                      )
                      .reduce(
                          (acc, el) => {
                              acc[el.name] = el.defaultValue;
                              return acc;
                          },
                          {} as { [key: string]: any }
                      );

        const mappedData = Object.keys(modalData).reduce(
            (acc: { [key: string]: any }, key) => {
                const value = modalData[key];
                if (value && value.id !== undefined) {
                    acc[key] = value.id;
                } else {
                    acc[key] = value;
                }
                return acc;
            },
            {}
        );

        const merged = {
            ...defaults,
            ...mappedData,
        };

        const useForm = hasFileInData(merged);
        const payload = useForm ? mappedDataToFormData(merged) : merged;

        return payload;
    };

    const handleAddData = async () => {
        try {
            const formData = mappedData();
            const response = await createItem(
                addApiUrl!,
                formData,
                keyProp,
                sortProp
            );

            let newItem: T | null = null;
            if (response && response.item) {
                newItem = response.item as T;
            } else {
                const newId = response?.newId ?? Date.now();
                newItem = { ...(formData as any), [keyProp]: newId } as T;
            }

            //   setFilteredData((prev) => {
            //     const next = sortByProperty([...prev, newItem as T], sortProp as keyof T);
            //     return next;
            //   });

            if (newItem) {
                const newData = [...(data ?? []), newItem];

                // 1) Notifica al padre con nueva lista
                onDataUpdate?.(newData);
                setModalData({});
                closeModal();
                setCurrentPage(1);
            }

            notificationRef.current?.addNotification(
                `${elementName} añadida correctamente`,
                "success"
            );
        } catch (err) {
            console.error("Error al añadir:", err);
            notificationRef.current?.addNotification(
                `Ha ocurrido un error al añadir ${elementName}`,
                "error"
            );
        }
    };

    const handleEditDataAPI = async (row: T) => {
        try {
            const id = (row as Record<string, any>)[keyProp];
            const payload = mappedData();
            const response = await updateItem(
                editApiUrl!,
                id,
                payload,
                keyProp,
                sortProp
            );

            // si el backend devuelve el item actualizado, úsalo; si no, combinamos
            let updatedItem: T | null = null;
            if (response && response.item) {
                updatedItem = response.item as T;
            } else {
                updatedItem = { ...(row as any), ...(payload as any) } as T;
            }

            const newData = (data ?? []).map((r: any) =>
                String(r[keyProp]) === String(id) ? updatedItem : r
            );

            // **Notifica al padre**
            onDataUpdate?.(newData);

            // setFilteredData((prev) =>
            //   prev.map((r) =>
            //     (r as any)[keyProp] === (updatedItem as any)[keyProp]
            //       ? (updatedItem as T)
            //       : r
            //   )
            // );

            notificationRef.current?.addNotification(
                `${elementName} editada correctamente`,
                "success"
            );

            closeModal();
        } catch (err) {
            console.error("Error al editar:", err);
            notificationRef.current?.addNotification(
                `Ha ocurrido un error al editar ${elementName}`,
                "error"
            );
        }
    };

    const handleDeleteData = async (row: T) => {
        setRowToDelete(row);
        setConfirmationModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!rowToDelete) return;
        try {
            const id = (rowToDelete as Record<string, any>)[keyProp];
            await deleteItem(deleteApiUrl!, id, keyProp, sortProp);

            const newSelection = new Set(selectedRows);

            newSelection.delete(id);

            const newData = (data ?? []).filter(
                (r: any) => String(r[keyProp]) !== String(id)
            );
            onDataUpdate?.(newData);

            onSelectionChange?.(newSelection);
            setConfirmationModalOpen(false);
            setRowToDelete(null);

            notificationRef.current?.addNotification(
                `${elementName} eliminada correctamente`,
                "success"
            );
        } catch (err) {
            console.error("Error al eliminar:", err);
            notificationRef.current?.addNotification(
                `Ha ocurrido un error al eliminar ${elementName}`,
                "error"
            );
            setConfirmationModalOpen(false);
            setRowToDelete(null);
        }
    };

    const calculateAssociatedOptions = (currentData: {
        [key: string]: any;
    }) => {
        const updatedOptions: { [key: string]: any[] } = {};

        formElements
            ?.flatMap((group) => group.elements)
            .forEach((element) => {
                if (
                    element.associatedFormElement &&
                    currentData[element.associatedFormElement]
                ) {
                    const parentId =
                        currentData[element.associatedFormElement]?.id ??
                        currentData[element.associatedFormElement];

                    if (!parentId) {
                        updatedOptions[element.name] = [];
                        return;
                    }

                    updatedOptions[element.name] =
                        element.options?.filter((option) => {
                            if (Array.isArray(option.fatherId)) {
                                return option.fatherId.includes(parentId);
                            }
                            return option.fatherId === parentId;
                        }) || [];
                }
            });

        return updatedOptions;
    };

    const handleEditData = (row: T) => {
        const initialData = { ...row };

        setModalData(initialData);

        const initialAssociatedOptions =
            calculateAssociatedOptions(initialData);
        setAssociatedOptions(initialAssociatedOptions);

        openModal();
        setModalType("edit");
    };

    const handleExportData = (row: T) => {
        exportObraReport({ row });
    };

    const handleInputChangeModal = (name: string, value: any) => {
        const normalized = normalizeDropdownValue(value);

        // 👇 si quieres que multiselect nunca sea null, conviértelo aquí
        const finalValue = normalized === null ? [] : normalized;

        setModalData((prev) => {
            const newModalData = { ...prev, [name]: finalValue };
            setAssociatedOptions(calculateAssociatedOptions(newModalData));
            return newModalData;
        });
    };

    const normalizeDropdownValue = (value: any) => {
        if (value == null) return [];

        // Multi
        if (Array.isArray(value)) {
            return value.map((v) =>
                v && typeof v === "object" && "value" in v ? v.value : v
            );
        }

        // Single
        if (typeof value === "object" && "value" in value) {
            return value.value;
        }

        return value;
    };

    useEffect(() => {
        const source = (data && Array.isArray(data) ? data : dataTable) ?? [];

        // 1) aplicar búsqueda primero
        let filtered = applyTableSearch(source, searchTerm, {
            searchTerms,
            columns,
        });

        const norm = (x: any) =>
            typeof x === "string" ? x.trim().toLowerCase() : x;

        const selectedValues = (arr: any[]) =>
            arr
                .map((v) =>
                    v && typeof v === "object" && "value" in v ? v.value : v
                )
                .map(norm);

        // 2) aplicar filtros sobre el resultado de la búsqueda
        Object.entries(filterValues).forEach(([key, val]) => {
            if (
                val === undefined ||
                val === "" ||
                (Array.isArray(val) && val.length === 0)
            ) {
                return;
            }

            const field = filterFields?.find((f) => f.key === key);
            if (!field) return;

            filtered = filtered.filter((row) => {
                const cell = (row as any)[key];

                if (field.type === "text") {
                    return String(cell ?? "")
                        .toLowerCase()
                        .includes(String(val ?? "").toLowerCase());
                }

                if (field.type === "number") {
                    return Number(cell) === Number(val);
                }

                if (
                    field.type === "date" &&
                    (val as any).start &&
                    (val as any).end
                ) {
                    return (
                        new Date(String(cell)) >= (val as any).start &&
                        new Date(String(cell)) <= (val as any).end
                    );
                }

                if (field.type === "select") {
                    return cell === val;
                }

                if (field.type === "multiselect" && Array.isArray(val)) {
                    const chosen = selectedValues(val);

                    if (cell && typeof cell === "object" && field.objectProp) {
                        return chosen.includes(
                            norm(
                                (cell as Record<string, any>)[field.objectProp]
                            )
                        );
                    }

                    return chosen.includes(norm(cell));
                }

                if (field.type === "cards" && Array.isArray(val)) {
                    const chosen = selectedValues(val);
                    return chosen.includes(norm(cell));
                }

                return true;
            });
        });

        setFilteredData(filtered);
    }, [
        data,
        dataTable,
        searchTerm,
        searchTerms,
        columns,
        filterValues,
        filterFields,
    ]);

    const clearFilters = () => {
        setFilterValues({});
        setIsFilterModalOpen(false);
    };

    const isEmptyValue = (elementType: string, value: any): boolean => {
        // null o undefined siempre vacío
        if (value === null || value === undefined) return true;

        switch (elementType) {
            case "text":
            case "email":
            case "password":
            case "tel":
            case "textarea":
                return String(value).trim() === "";

            case "number":
                // acepta 0 como valor válido si es requerido
                return String(value).trim() === "" || isNaN(Number(value));

            case "checkbox":
                // si es requerido, debe ser true
                return value !== true;

            case "radio":
                // debe existir un valor seleccionado no vacío
                return String(value).trim() === "";

            case "select":
                // soporta tanto primitivo como objeto {id|value}
                if (Array.isArray(value)) return value.length === 0; // por si 'withCheckbox' habilita múltiples
                if (typeof value === "object") {
                    return value.value === undefined && value.id === undefined;
                }
                return String(value).trim() === "";

            case "multiselect":
                // array con al menos un valor
                if (!Array.isArray(value)) return true;
                return value.length === 0;

            case "document":
                // depende de cómo lo guardes: base64, File, FormData...
                // criterio común: debe existir algún contenido no vacío
                if (typeof value === "string") return value.trim() === ""; // base64
                if (value && typeof value === "object") {
                    // File o similar
                    if ("size" in value) return (value.size as number) <= 0;
                    // objeto con campos: intenta detectar 'file'/'base64'
                    if ("file" in value) return !value.file;
                    if ("base64" in value) return !value.base64;
                }
                return !value;

            case "date":
                // cadena de fecha válida
                const str = String(value).trim();
                if (str === "") return true;
                const d = new Date(str);
                return isNaN(d.getTime());

            default:
                // por defecto: cadena vacía
                return String(value).trim() === "";
        }
    };

    const getMissingRequiredFields = (): { name: string; label?: string }[] => {
        const groups = (formElements ?? []).filter((g) =>
            g.elements?.some((e) => e.visible)
        );

        const missing: { name: string; label?: string }[] = [];

        groups.forEach((group) => {
            group.elements
                .filter((el) => el.visible && el.required)
                .forEach((el) => {
                    const val = modalData[el.name];
                    if (isEmptyValue(el.type, val)) {
                        missing.push({ name: el.name, label: el.label });
                    }
                });
        });

        return missing;
    };

    const validateRequiredFields = (): boolean => {
        const missing = getMissingRequiredFields();
        if (missing.length > 0) {
            const names = missing.map((m) => m.name);
            setMissingNames(names);
            const fieldsList = missing.map((m) => m.label ?? m.name).join(", ");
            notificationRef.current?.addNotification(
                `Por favor, completa los campos requeridos: ${fieldsList}`,
                "info"
            );
            return false;
        }
        setMissingNames([]);
        return true;
    };

    const exportObraReport = async ({ row }: { row: T }) => {
        const api = getApiInstance();

        const CodObra = (row as any)[keyProp];
        const CodZona = selectedZone?.value || 0;
        const CodZonaLegal = selectedZone?.value || 0;
        const extraProp = (row as any)[extraPropExportKey] || null;
        const extraPropText = extraPropExportText;

        console.log(row);

        console.log("Iniciando exportación con:", {
            keyProp: CodObra,
            CodZona,
            CodZonaLegal,
            extraProp: extraProp ?? "N/A",
        });

        try {
            const response = await api.post(
                exportApiUrl,
                null, // cuerpo vacío; los parámetros van en query string
                {
                    params: {
                        [keyProp]: CodObra,
                        CodZona,
                        CodZonaLegal,
                        ...(extraProp != null && {
                            [extraPropText]: extraProp,
                        }),
                    },
                    responseType: "blob",
                }
            );

            // Si por alguna razón el servidor devolviera JSON, intenta interpretarlo.
            const contentType = (
                response.headers["content-type"] || ""
            ).toLowerCase();
            if (contentType.includes("application/json")) {
                const text = await response.data.text();
                let json;
                try {
                    json = JSON.parse(text);
                } catch {
                    json = { message: text };
                }
                throw new Error(
                    json?.message ||
                        "El servidor respondió con JSON en lugar de un archivo."
                );
            }

            // Nombre de archivo desde Content-Disposition (soporta filename y filename*)
            const cd = response.headers["content-disposition"] || "";
            let fileName = `Informe_${CodObra}.xlsx`;

            const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);

            const normalMatch = cd.match(/filename="?([^"]+)"?/i);

            const rawFileName = utf8Match?.[1] || normalMatch?.[1];

            if (rawFileName) {
                try {
                    fileName = decodeURIComponent(rawFileName.trim());
                } catch {
                    fileName = rawFileName.trim();
                }
            }
            // Blob
            const blob =
                response.data instanceof Blob
                    ? response.data
                    : new Blob([response.data], {
                          type:
                              response.headers["content-type"] ||
                              "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                      });

            const urlBlob = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = urlBlob;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(urlBlob);

            return { ok: true, fileName, size: blob.size };
        } catch (err) {
            // Si tienes un handler global (setError), úsalo; aquí dejamos retorno estructurado
            console.error(err);
            return { ok: false, error: err };
        }
    };

    return (
        <div className="table-card">
            <div className="table-card__top-content">
                <div style={{ flex: "1" }}>
                    {searchTerms.length > 0 && (
                        <div style={{ maxWidth: "400px" }}>
                            <InputSearch
                                placeholder={`${t(
                                    "common.search"
                                )} ${elementName?.toLowerCase()}...`}
                                onSearch={handleSearch}
                            />
                        </div>
                    )}
                </div>
                <div
                    style={{
                        display: "flex",
                        gap: "12px",
                        alignItems: "center",
                        marginLeft: "auto",
                    }}
                >
                    {hasFilters && (
                        <Button
                            variant="ghost"
                            startIcon={MdFilterList({})}
                            onClick={() => setIsFilterModalOpen(true)}
                            endIcon={undefined}
                            iconOnly={isSmallMobile}
                        >
                            {t("common.filters")}
                        </Button>
                    )}

                    {canChangeState && effectiveSelectedRows.size > 0 && (
                        <Button
                            variant="filled"
                            onClick={openBulkStateModal}
                            endIcon={undefined}
                            startIcon={MdOutlineEdit({})}
                        >
                            Cambiar estado ({effectiveSelectedRows.size})
                        </Button>
                    )}

                    {formElements != undefined &&
                        formElements?.length > 0 &&
                        userCodAcceso === 1 &&
                        userCodPerfil !== 4 && (
                            <Button
                                variant="filled"
                                startIcon={IoMdAdd({})}
                                onClick={openModal}
                                endIcon={undefined}
                            >
                                {`${t("common.add")} ${elementName}`}
                            </Button>
                        )}

                    {extraTopButtons}
                </div>
            </div>

            <div className="table-card__content">
                <div className="table-container">
                    <table className={getTableClasses()}>
                        <thead>
                            <tr>
                                {expandable && isMobile && (
                                    <th className="expand-column"></th>
                                )}
                                {enableRowSelection && (
                                    <th className={getColumnClass(true, false)}>
                                        <div
                                            className={`table-checkbox ${
                                                selectAll ? "checked" : ""
                                            }`}
                                            onClick={handleSelectAll}
                                        />
                                    </th>
                                )}
                                {columns
                                    .filter(
                                        (x) =>
                                            x.visible &&
                                            !(isMobile && x.visibleOnExpand)
                                    )
                                    .map((col) => (
                                        <th
                                            key={String(col.key)}
                                            className={`${getColumnClass(
                                                false,
                                                false
                                            )} ${
                                                col.sortable !== false
                                                    ? "sortable"
                                                    : ""
                                            }`}
                                            onClick={() =>
                                                col.sortable !== false &&
                                                requestSort(
                                                    getNestedValue(col, col.key)
                                                )
                                            }
                                        >
                                            {col.label}
                                            {sortConfig.key === col.key
                                                ? sortConfig.direction ===
                                                  "ascending"
                                                    ? " ▲"
                                                    : " ▼"
                                                : null}
                                        </th>
                                    ))}
                                {actions &&
                                    userCodAcceso === 1 &&
                                    userCodPerfil !== 4 && (
                                        <th
                                            className={getColumnClass(
                                                false,
                                                true
                                            )}
                                        >
                                            {t("common.actions")}
                                        </th>
                                    )}
                            </tr>
                        </thead>
                        <tbody>
                            {loadingData ? (
                                Array.from({ length: rowsPerPage }).map(
                                    (_, rowIdx) => (
                                        <tr key={`skeleton-${rowIdx}`}>
                                            {expandable && isMobile && (
                                                <td className="expand-column skeleton-cell"></td>
                                            )}
                                            {enableRowSelection && (
                                                <td className="checkbox-column skeleton-cell"></td>
                                            )}
                                            {columns
                                                .filter((c) =>
                                                    isMobile
                                                        ? c.visible &&
                                                          !c.visibleOnExpand
                                                        : c.visible
                                                )
                                                .map((col, colIdx) => (
                                                    <td
                                                        key={`skeleton-${rowIdx}-${colIdx}`}
                                                        className={`content-column skeleton-cell`}
                                                    ></td>
                                                ))}
                                            {actions &&
                                                userCodAcceso === 1 &&
                                                userCodPerfil !== 4 && (
                                                    <td className="actions-column skeleton-cell"></td>
                                                )}
                                        </tr>
                                    )
                                )
                            ) : currentRows.length > 0 ? (
                                currentRows.map((row, index) => {
                                    const rowId =
                                        (row[keyProp as keyof T] as number) ||
                                        index;
                                    const isExpanded = expandedRows.has(rowId);

                                    return (
                                        <React.Fragment key={rowId}>
                                            <tr
                                                className={`${
                                                    effectiveSelectedRows.has(
                                                        rowId
                                                    )
                                                        ? "selected"
                                                        : ""
                                                } ${
                                                    isExpanded ? "expanded" : ""
                                                }`}
                                            >
                                                {expandable && isMobile && (
                                                    <td className="expand-column">
                                                        <div
                                                            className="expand-icon"
                                                            onClick={() =>
                                                                toggleRowExpansion(
                                                                    rowId
                                                                )
                                                            }
                                                        >
                                                            {isExpanded
                                                                ? MdKeyboardArrowUp(
                                                                      {
                                                                          size: 20,
                                                                      }
                                                                  )
                                                                : MdKeyboardArrowDown(
                                                                      {
                                                                          size: 20,
                                                                      }
                                                                  )}
                                                        </div>
                                                    </td>
                                                )}
                                                {enableRowSelection && (
                                                    <td
                                                        className={getColumnClass(
                                                            true,
                                                            false
                                                        )}
                                                    >
                                                        <div
                                                            className={`table-checkbox ${
                                                                effectiveSelectedRows.has(
                                                                    rowId
                                                                )
                                                                    ? "checked"
                                                                    : ""
                                                            }`}
                                                            onClick={() =>
                                                                handleSelectRow(
                                                                    rowId
                                                                )
                                                            }
                                                        />
                                                    </td>
                                                )}
                                                {columns
                                                    .filter(
                                                        (x) =>
                                                            x.visible &&
                                                            !(
                                                                isMobile &&
                                                                x.visibleOnExpand
                                                            )
                                                    )
                                                    .map((col) => (
                                                        <td
                                                            key={`${rowId}-${String(
                                                                col.key
                                                            )}`}
                                                            title={String(
                                                                getNestedValue(
                                                                    row,
                                                                    col.key
                                                                )
                                                            )}
                                                            className={`${getColumnClass(
                                                                false,
                                                                false
                                                            )} ${
                                                                col.align
                                                                    ? `text-${col.align}`
                                                                    : ""
                                                            }`}
                                                            style={{
                                                                ...(col.width && {
                                                                    width: col.width,
                                                                    minWidth:
                                                                        col.width,
                                                                    maxWidth:
                                                                        col.width,
                                                                }),
                                                            }}
                                                        >
                                                            {renderCellValue(
                                                                row,
                                                                col
                                                            )}
                                                        </td>
                                                    ))}
                                                {actions &&
                                                    userCodAcceso === 1 &&
                                                    userCodPerfil !== 4 && (
                                                        <td
                                                            className={getColumnClass(
                                                                false,
                                                                true
                                                            )}
                                                        >
                                                            <OptionsMenu
                                                                row={row}
                                                                options={
                                                                    actions?.map(
                                                                        (
                                                                            action
                                                                        ) => {
                                                                            if (
                                                                                action ===
                                                                                "Eliminar"
                                                                            ) {
                                                                                return {
                                                                                    icon: MdDelete(
                                                                                        {}
                                                                                    ),
                                                                                    label: action,
                                                                                    action: () =>
                                                                                        handleDeleteData(
                                                                                            row
                                                                                        ),
                                                                                };
                                                                            } else if (
                                                                                action ===
                                                                                "Editar"
                                                                            ) {
                                                                                return {
                                                                                    icon: MdEdit(
                                                                                        {}
                                                                                    ),
                                                                                    label: action,
                                                                                    action: () =>
                                                                                        handleEditData(
                                                                                            row
                                                                                        ),
                                                                                };
                                                                            } else if (
                                                                                action ===
                                                                                "Exportar"
                                                                            ) {
                                                                                return {
                                                                                    icon: MdOutlineFileDownload(
                                                                                        {}
                                                                                    ),
                                                                                    label: t(
                                                                                        "common.export"
                                                                                    ),
                                                                                    action: () =>
                                                                                        handleExportData(
                                                                                            row
                                                                                        ),
                                                                                };
                                                                            } else if (
                                                                                typeof action ===
                                                                                "object"
                                                                            ) {
                                                                                // ✅ Acción custom con render propio
                                                                                if (
                                                                                    action.render
                                                                                ) {
                                                                                    return {
                                                                                        label: action.label,
                                                                                        icon: action.icon,
                                                                                        render: () =>
                                                                                            action.render!(
                                                                                                row
                                                                                            ),
                                                                                    };
                                                                                }
                                                                                // ✅ Acción custom con handler
                                                                                return {
                                                                                    label: action.label,
                                                                                    icon: action.icon,
                                                                                    action: action.action
                                                                                        ? () =>
                                                                                              action.action!(
                                                                                                  row
                                                                                              )
                                                                                        : () => {},
                                                                                };
                                                                            } else {
                                                                                return {
                                                                                    label: action as string,
                                                                                    action: () =>
                                                                                        console.log(
                                                                                            `${action} action clicked`
                                                                                        ),
                                                                                };
                                                                            }
                                                                        }
                                                                    ) ?? []
                                                                }
                                                            />
                                                        </td>
                                                    )}
                                            </tr>
                                            {expandable &&
                                                isMobile &&
                                                isExpanded && (
                                                    <tr className="expanded-row">
                                                        <td
                                                            colSpan={
                                                                columns.filter(
                                                                    (c) =>
                                                                        c.visible &&
                                                                        !c.visibleOnExpand
                                                                ).length +
                                                                (enableRowSelection
                                                                    ? 1
                                                                    : 0) +
                                                                (expandable &&
                                                                isMobile
                                                                    ? 1
                                                                    : 0) +
                                                                (actions &&
                                                                actions.length >
                                                                    0
                                                                    ? 1
                                                                    : 0)
                                                            }
                                                            className="expanded-content"
                                                        >
                                                            {renderExpandedContent ? (
                                                                renderExpandedContent(
                                                                    row
                                                                )
                                                            ) : (
                                                                <div className="expanded-content__grid">
                                                                    {columns
                                                                        .filter(
                                                                            (
                                                                                col
                                                                            ) =>
                                                                                col.visibleOnExpand
                                                                        )
                                                                        .map(
                                                                            (
                                                                                col
                                                                            ) => (
                                                                                <div
                                                                                    key={String(
                                                                                        col.key
                                                                                    )}
                                                                                    className="expanded-content__item"
                                                                                >
                                                                                    <span className="expanded-content__label">
                                                                                        {
                                                                                            col.label
                                                                                        }

                                                                                        :
                                                                                    </span>
                                                                                    <span className="expanded-content__value">
                                                                                        {renderCellValue(
                                                                                            row,
                                                                                            col
                                                                                        )}
                                                                                    </span>
                                                                                </div>
                                                                            )
                                                                        )}
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                        </React.Fragment>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td
                                        colSpan={
                                            columns.filter(
                                                (c) =>
                                                    c.visible &&
                                                    !(
                                                        isMobile &&
                                                        c.visibleOnExpand
                                                    )
                                            ).length +
                                            (actions ? 1 : 0) +
                                            (enableRowSelection ? 1 : 0) +
                                            (expandable && isMobile ? 1 : 0)
                                        }
                                        className="no-results-cell"
                                    >
                                        <div className="no-results-cell__content">
                                            {MdSearchOff({
                                                className:
                                                    "no-results-cell__content__icon",
                                            })}
                                            <p className="no-results-cell__content__text">
                                                {noResultsMessage ||
                                                    "No se han encontrado resultados."}
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="table-card__pagination">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            itemsPerPage={rowsPerPage}
                            totalItems={filteredData.length}
                            onPageChange={setCurrentPage}
                        />
                    </div>
                )}
                <Modal
                    isOpen={isModalOpen}
                    onClose={closeModal}
                    position="side"
                    actions={
                        <>
                            <Button
                                variant="outlined"
                                onClick={closeModal}
                                endIcon={undefined}
                                startIcon={undefined}
                            >
                                {t("common.cancel")}
                            </Button>
                            <Button
                                variant="filled"
                                onClick={() => {
                                    // 1) validar requeridos
                                    const ok = validateRequiredFields();
                                    if (!ok) return;

                                    // 2) continuar con la acción correspondiente
                                    if (modalType === "edit") {
                                        handleEditDataAPI(modalData as T);
                                    } else {
                                        handleAddData();
                                    }
                                }}
                                endIcon={undefined}
                                startIcon={undefined}
                            >
                                {t("common.save")}
                            </Button>
                        </>
                    }
                    title={
                        modalType === "edit"
                            ? `${t("common.edit")} ${elementName}`
                            : `${t("common.add")} ${elementName}`
                    }
                >
                    <FormModal
                        dynamicFormElements={dynamicFormElements}
                        modalData={modalData}
                        associatedOptions={associatedOptions}
                        handleInputChange={handleInputChangeModal}
                        missingNames={missingNames}
                    />
                </Modal>

                {isFilterModalOpen && filterFields && hasFilters && (
                    <Modal
                        position="side"
                        isOpen
                        onClose={() => setIsFilterModalOpen(false)}
                        actions={
                            <>
                                <Button
                                    variant="outlined"
                                    onClick={clearFilters}
                                    endIcon={undefined}
                                    startIcon={undefined}
                                >
                                    {t("common.clean")}
                                </Button>
                                <Button
                                    variant="filled"
                                    onClick={() => setIsFilterModalOpen(false)}
                                    endIcon={undefined}
                                    startIcon={undefined}
                                >
                                    {t("common.apply")}
                                </Button>
                            </>
                        }
                    >
                        <FiltersDrawer
                            filterFields={filterFields}
                            data={data ?? []}
                            filterValues={filterValues}
                            onChange={(key: any, val: any) =>
                                setFilterValues((prev) => ({
                                    ...prev,
                                    [key]: val,
                                }))
                            }
                            onClear={clearFilters}
                        />
                    </Modal>
                )}

                {confirmationModalOpen && (
                    <Modal
                        isOpen={confirmationModalOpen}
                        onClose={() => setConfirmationModalOpen(false)}
                        position="center"
                        actions={
                            <>
                                <Button
                                    variant="outlined"
                                    onClick={() =>
                                        setConfirmationModalOpen(false)
                                    }
                                    endIcon={undefined}
                                    startIcon={undefined}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    variant="filled"
                                    onClick={confirmDelete}
                                    endIcon={undefined}
                                    startIcon={undefined}
                                >
                                    Confirmar
                                </Button>
                            </>
                        }
                        title="Confirmar eliminación"
                    >
                        <p className="modal-description">
                            ¿Estás seguro de que deseas eliminar este elemento?
                        </p>
                    </Modal>
                )}
            </div>

            {isStateModalOpen && (
                <Modal
                    isOpen={isStateModalOpen}
                    onClose={closeStateModal}
                    position="center"
                    actions={
                        <div
                            className="modal-actions"
                            style={{ marginTop: 16 }}
                        >
                            <Button
                                variant="filled"
                                onClick={confirmStateChange}
                                startIcon={undefined}
                                endIcon={undefined}
                            >
                                {isChangingState
                                    ? "Actualizando..."
                                    : "Aceptar"}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={closeStateModal}
                                startIcon={undefined}
                                endIcon={undefined}
                            >
                                Cancelar
                            </Button>
                        </div>
                    }
                    title="Cambiar estado"
                >
                    <InputDropdown_New
                        label="Nuevo estado"
                        options={stateChangeOptions || []}
                        defaultValue={newStateValue}
                        onChange={(value: any) => setNewStateValue(value)}
                        disabled={isChangingState}
                    />
                    <InputTextarea
                        label="Observaciones"
                        value={observations}
                        onChange={(e) => setObservations(e.target.value)}
                        rows={6}
                    />
                </Modal>
            )}

            <Notification ref={notificationRef} />
        </div>
    );
}

export default Table;
